import { isContentClean, getFilterMessage } from '../utils/contentFilter';
import React, { useState, useEffect } from 'react';
import {
  Container,
  VStack,
  HStack,
  Box,
  Text,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Alert,
  AlertIcon,
  useToast,
  Divider,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  PinInput,
  PinInputField,
  Spinner,
  Link,
  Button,
  Input,
  Checkbox,
} from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  sendEmailVerification,
  sendPasswordResetEmail,
} from 'firebase/auth';
import { auth, db, signInWithGoogleNative, signInWithAppleNative } from '../services/firebase';
import { isStoreSafe } from '../utils/storeSafe';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import walletService from '../services/walletService';
import userService from '../services/userService';
import { API_BASE } from '../utils/apiBase';
import { POKER_COLORS } from '../theme/designSystem';
import PokerCard from '../components/ui/PokerCard';
import PokerInput from '../components/ui/PokerInput';
import PokerButton from '../components/ui/PokerButton';

// ============================================================================
//  INVITE SERVICE - Handle pending invites after login

// ============================================================================
import { 
  processPendingInviteAfterLogin, 
  hasPendingInvite,
  storePostOnboardingRedirect,
  clearPendingInvite,
} from '../services/inviteService';

// ============================================================================
//  CAPACITOR PLATFORM DETECTION

// ============================================================================
import { Capacitor } from '@capacitor/core';
import SocialLinks from '../components/ui/SocialLinks';

// ============================================================================
//  TWO-FACTOR AUTHENTICATION SERVICE

// ============================================================================
import {
  check2FARequired,
  verifyLoginCode,
  checkLoginRateLimit,
  recordFailedLogin,
  requestEmailRecovery,
  verifyEmailRecovery,
} from '../services/twoFactorService';

// Check if running on native mobile platform
const isNativePlatform = Capacitor.isNativePlatform();

//  API URL for secure wallet operations (routed via shared apiBase util)

const API_BASE_URL = API_BASE;
// Bump when the Official Sweepstakes Rules change so acceptance can be re-asked.
const RULES_VERSION = '2026-09';

//  Call the secure /creditReferralBonus API endpoint

const creditReferralBonusAPI = async (userId: string): Promise<boolean> => {
  const user = auth.currentUser;
  if (!user) return false;
  
  try {
    const token = await user.getIdToken();
    const response = await fetch(`${API_BASE_URL}/creditReferralBonus`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ userId }),
    });
    
    const result = await response.json();
    return result.success || result.alreadyCredited;
  } catch (error) {
    console.error('Error crediting referral bonus:', error);
    return false;
  }
};

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50% { transform: translateY(-15px) rotate(5deg); }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0px) translateX(0px) rotate(-3deg); }
  25% { transform: translateY(-20px) translateX(10px) rotate(2deg); }
  50% { transform: translateY(-10px) translateX(-5px) rotate(-2deg); }
  75% { transform: translateY(-25px) translateX(5px) rotate(3deg); }
`;

const floatDrift = keyframes`
  0%, 100% { transform: translateY(0px) translateX(0px) scale(1); opacity: 0.12; }
  50% { transform: translateY(-30px) translateX(20px) scale(1.05); opacity: 0.18; }
`;

const pulseBig = keyframes`
  0%, 100% { transform: scale(1); opacity: 0.15; }
  50% { transform: scale(1.1); opacity: 0.25; }
`;

const shimmer = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [appleLoading, setAppleLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const toast = useToast();

  //  Referral code state

  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState('');
  const [showReferralInput, setShowReferralInput] = useState(false);
  const [referralCodeValid, setReferralCodeValid] = useState<boolean | null>(null);
  const [validatingCode, setValidatingCode] = useState(false);
  const [referrerId, setReferrerId] = useState<string | null>(null);

  //  Password reset modal

  const { isOpen: isResetOpen, onOpen: onResetOpen, onClose: onResetClose } = useDisclosure();

  // Official Sweepstakes Rules acceptance (required to create an account).
  // The tick is stashed in sessionStorage so a Google/Apple redirect sign-up
  // can still record it once the profile exists.
  const [acceptedRules, setAcceptedRules] = useState<boolean>(() => {
    try { return sessionStorage.getItem('pss:rulesAccepted') === '1'; } catch { return false; }
  });
  const { isOpen: isRulesOpen, onOpen: onRulesOpen, onClose: onRulesClose } = useDisclosure();
  const setRulesAccepted = (v: boolean) => {
    setAcceptedRules(v);
    try { v ? sessionStorage.setItem('pss:rulesAccepted', '1') : sessionStorage.removeItem('pss:rulesAccepted'); } catch { /* noop */ }
  };
  const requireRules = (): boolean => {
    if (acceptedRules) return true;
    setError('Please read and accept the Official Sweepstakes Rules to create an account.');
    onRulesOpen();
    return false;
  };
  const recordRulesAcceptance = async (uid: string) => {
    let ticked = acceptedRules;
    try { ticked = ticked || sessionStorage.getItem('pss:rulesAccepted') === '1'; } catch { /* noop */ }
    if (!ticked) return;
    try {
      await setDoc(doc(db, 'users', uid), {
        sweepstakesRulesAcceptedAt: serverTimestamp(),
        sweepstakesRulesVersion: RULES_VERSION,
      }, { merge: true });
      try { sessionStorage.removeItem('pss:rulesAccepted'); } catch { /* noop */ }
    } catch (e) {
      console.warn('[SIGNUP] Could not record rules acceptance (will not block signup):', e);
    }
  };
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  //  Email verification state

  const [showVerificationWarning, setShowVerificationWarning] = useState(false);
  const [resendingVerification, setResendingVerification] = useState(false);

  // =========================================================================
  //  TWO-FACTOR AUTHENTICATION STATE

  // =========================================================================
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState('');
  const [pendingCredential, setPendingCredential] = useState<any>(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null);
  const [lockoutUntil, setLockoutUntil] = useState<Date | null>(null);
  const [lockoutCountdown, setLockoutCountdown] = useState<string>('');
  
  // Email recovery state
  const [showEmailRecovery, setShowEmailRecovery] = useState(false);
  const [emailRecoveryCode, setEmailRecoveryCode] = useState('');
  const [emailRecoverySent, setEmailRecoverySent] = useState(false);
  const [emailRecoveryLoading, setEmailRecoveryLoading] = useState(false);
  const [emailRecoveryError, setEmailRecoveryError] = useState('');

  // ============================================================================
  //  INVITE REDIRECT STATE

  // ============================================================================
  const isInviteRedirect = searchParams.get('redirect') === 'invite';
  const hasPendingInviteToken = hasPendingInvite();

  // =========================================================================
  //  LOCKOUT COUNTDOWN TIMER

  // =========================================================================
  useEffect(() => {
    if (!lockoutUntil) {
      setLockoutCountdown('');
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const diff = lockoutUntil.getTime() - now.getTime();
      
      if (diff <= 0) {
        setLockoutUntil(null);
        setLockoutCountdown('');
        return;
      }

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      if (hours > 0) {
        setLockoutCountdown(`${hours}h ${minutes}m ${seconds}s`);
      } else if (minutes > 0) {
        setLockoutCountdown(`${minutes}m ${seconds}s`);
      } else {
        setLockoutCountdown(`${seconds}s`);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [lockoutUntil]);

  // ============================================================================
  //  HANDLE PENDING INVITE AFTER LOGIN

  // ============================================================================
  const handlePendingInvite = async (isNewUser: boolean = false): Promise<string | null> => {
    if (!hasPendingInviteToken) return null;

    try {
      console.log('🔗 [LOGIN] Processing pending invite...');
      const { redirectUrl } = await processPendingInviteAfterLogin();
      
      if (redirectUrl) {
        if (isNewUser) {
          // New users need to complete onboarding first
          // Store the redirect for after onboarding
          storePostOnboardingRedirect(redirectUrl);
          console.log('🔗 [LOGIN] New user - storing redirect for post-onboarding:', redirectUrl);
          return null; // Let them go to onboarding first
        } else {
          console.log('🔗 [LOGIN] Existing user - redirecting to:', redirectUrl);
          return redirectUrl;
        }
      }
    } catch (error) {
      console.error('❌ [LOGIN] Error processing pending invite:', error);
      clearPendingInvite();
    }
    
    return null;
  };

  //  Capture referral code from URL on mount

  useEffect(() => {
    const refCode = searchParams.get('ref');
    if (refCode) {
      setReferralCode(refCode.toUpperCase());
      setShowReferralInput(true);
      // Validate the code and get the referrer's user ID
      setValidatingCode(true);
      userService.findUserByReferralCode(refCode).then(userId => {
        setReferralCodeValid(!!userId);
        setReferrerId(userId);
        setValidatingCode(false);
      }).catch(() => {
        setReferralCodeValid(false);
        setReferrerId(null);
        setValidatingCode(false);
      });
    }
  }, [searchParams]);

  // ============================================================================
  //  MOBILE OAUTH: Handle redirect result on native platforms

  // ============================================================================
  useEffect(() => {
    const handleRedirectResult = async () => {
      //  iOS FIX: Skip redirect result on native platforms

      // Native uses @capgo/capacitor-social-login, not redirect flow
      if (isNativePlatform) {
        return;
      }
      
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          const user = result.user;
          setGoogleLoading(true);
          
          // Check if user profile exists
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          
          if (!userDoc.exists()) {
            // New user - create profile and wallet
            const displayName = user.displayName || user.email?.split('@')[0] || 'Player';
            
            await userService.createUserProfile(
              user.uid, 
              user.email || '', 
              displayName,
              referrerId || undefined
            );
            await walletService.createWallet(user.uid);
            await recordRulesAcceptance(user.uid);
            
            //  Credit $5 PSS Coins referral bonus with 1x playthrough

            if (referralCodeValid && referralCode) {
              await creditReferralBonusAPI(user.uid);
              toast({
                title: 'Account Created with Bonus!',

                description: `Welcome ${displayName}! 1,000 PlayCoins + 5 FREE PSS Coins bonus!`,
                status: 'success',
                duration: 4000,
                isClosable: true,
              });
            } else {
              toast({
                title: 'Account Created!',

                description: `Welcome ${displayName}! 1,000 PlayCoins credited!`,
                status: 'success',
                duration: 3000,
                isClosable: true,
              });
            }
            
            //  Handle pending invite for new users

            await handlePendingInvite(true);
            
            // Navigate to onboarding for new users
            navigate('/onboarding');
            return;
          } else {
            toast({
              title: 'Welcome back!',

              description: 'Good luck at the tables!',
              status: 'success',
              duration: 2000,
              isClosable: true,
            });
          }
          
          //  Check for pending invite before redirecting

          const inviteRedirect = await handlePendingInvite(false);
          navigate(inviteRedirect || '/');
        }
      } catch (err: any) {
        console.error('Redirect result error:', err);
        //  iOS FIX: Also ignore auth/argument-error (no redirect pending on native)

        if (err.code !== 'auth/popup-closed-by-user' && 
            err.code !== 'auth/argument-error') {
          setError(err.message || 'Sign in failed');
        }
      } finally {
        setGoogleLoading(false);
      }
    };

    handleRedirectResult();
  }, [navigate, toast, referralCode, referralCodeValid, referrerId]);

  // ============================================================================
  //  2FA VERIFICATION HANDLER

  // ============================================================================
  const handle2FAVerify = async () => {
    if (!pendingCredential || !twoFactorCode) return;
    
    setTwoFactorLoading(true);
    setTwoFactorError('');
    
    try {
      // Get email - handle both Google (result.user.email) and Email ({email, password}) structures
      const userEmail = pendingCredential.email || pendingCredential.user?.email;
      
      if (!userEmail) {
        setTwoFactorError('Unable to verify - email not found');
        setTwoFactorLoading(false);
        return;
      }
      
      // Verify the 2FA code with backend
      const result = await verifyLoginCode(userEmail, twoFactorCode);
      
      if (result.success) {
        // Re-authenticate for email login (Google is already authenticated)
        if (pendingCredential.password) {
          await signInWithEmailAndPassword(auth, pendingCredential.email, pendingCredential.password);
        }
        // For Google login, user is already authenticated - just proceed
        
        setShow2FAModal(false);
        setTwoFactorCode('');
        setPendingCredential(null);
        
        toast({
          title: 'Welcome back!',

          description: 'Good luck at the tables!',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        
        // Show warning if backup code was used
        if (result.error) {
          toast({
            title: 'Backup Code Used',

            description: result.error,
            status: 'warning',
            duration: 8000,
            isClosable: true,
          });
        }
        
        //  Check for pending invite before redirecting

        const inviteRedirect = await handlePendingInvite(false);
        navigate(inviteRedirect || '/');
      } else {
        // Record failed attempt
        await recordFailedLogin(userEmail);
        
        // Check updated rate limit
        const rateLimit = await checkLoginRateLimit(userEmail);
        if (rateLimit.locked && rateLimit.lockedUntil) {
          setLockoutUntil(rateLimit.lockedUntil);
          const mins = Math.ceil((rateLimit.lockedUntil.getTime() - Date.now()) / 60000);
          setTwoFactorError(`Too many failed attempts. Try again in ${mins} minutes.`);
        } else {
          if (rateLimit.remainingAttempts !== undefined) {
            setAttemptsRemaining(rateLimit.remainingAttempts);
          }
          setTwoFactorError(result.error || 'Invalid code. Please try again.');
        }
      }
    } catch (err: any) {
      console.error('2FA verification error:', err);
      setTwoFactorError(err.message || '2FA verification failed');
    } finally {
      setTwoFactorLoading(false);
    }
  };

  // ============================================================================
  //  EMAIL RECOVERY HANDLERS

  // ============================================================================
  const handleRequestEmailRecovery = async () => {
    const userEmail = pendingCredential?.email || pendingCredential?.user?.email;
    if (!userEmail) return;
    
    setEmailRecoveryLoading(true);
    setEmailRecoveryError('');
    
    try {
      const result = await requestEmailRecovery(userEmail);
      
      if (result.success) {
        setEmailRecoverySent(true);
        toast({
          title: 'Recovery Code Sent',

          description: 'Check your email for the 8-character recovery code.',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        setEmailRecoveryError(result.error || 'Failed to send recovery code');
      }
    } catch (err: any) {
      console.error('Email recovery request error:', err);
      setEmailRecoveryError(err.message || 'Failed to send recovery code');
    } finally {
      setEmailRecoveryLoading(false);
    }
  };

  const handleVerifyEmailRecovery = async () => {
    const userEmail = pendingCredential?.email || pendingCredential?.user?.email;
    if (!userEmail || !emailRecoveryCode) return;
    
    setEmailRecoveryLoading(true);
    setEmailRecoveryError('');
    
    try {
      const result = await verifyEmailRecovery(
        userEmail,
        emailRecoveryCode
      );
      
      if (result.success) {
        // Re-authenticate if we have password (email login)
        if (pendingCredential?.password) {
          await signInWithEmailAndPassword(auth, pendingCredential.email, pendingCredential.password);
        }
        
        setShow2FAModal(false);
        setShowEmailRecovery(false);
        setEmailRecoveryCode('');
        setPendingCredential(null);
        
        toast({
          title: 'Welcome back!',

          description: 'Consider setting up a new authenticator in Profile > Security.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        
        //  Check for pending invite before redirecting

        const inviteRedirect = await handlePendingInvite(false);
        navigate(inviteRedirect || '/');
      } else {
        setEmailRecoveryError(result.error || 'Invalid recovery code');
      }
    } catch (err: any) {
      console.error('Email recovery verification error:', err);
      setEmailRecoveryError(err.message || 'Recovery verification failed');
    } finally {
      setEmailRecoveryLoading(false);
    }
  };

  // ============================================================================
  // SIGN IN HANDLER (with 2FA support)
  // ============================================================================
  const handleSignIn = async () => {
    setError('');
    setShowVerificationWarning(false);
    setLoading(true);

    try {
      //  Check rate limit before attempting login

      const rateLimit = await checkLoginRateLimit(email);
      if (rateLimit.locked && rateLimit.lockedUntil) {
        setLockoutUntil(rateLimit.lockedUntil);
        const mins = Math.ceil((rateLimit.lockedUntil.getTime() - Date.now()) / 60000);
        setError(`Account temporarily locked. Try again in ${mins} minutes.`);
        setLoading(false);
        return;
      }
      
      // Show warning if running low on attempts
      if (rateLimit.remainingAttempts !== undefined && rateLimit.remainingAttempts <= 2) {
        setAttemptsRemaining(rateLimit.remainingAttempts);
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      
      //  CHECK EMAIL VERIFICATION for email/password signups

      if (!userCredential.user.emailVerified) {
        setShowVerificationWarning(true);
        setLoading(false);
        return;
      }
      
      //  Check if 2FA is required

      const requires2FA = await check2FARequired(email);
      
      if (requires2FA) {
        //  CRITICAL: Sign out immediately to prevent 2FA bypass

        // User must complete 2FA before being considered authenticated
        await auth.signOut();
        
        // Store email for 2FA verification (not the credential)
        setPendingCredential({ email, password });
        setShow2FAModal(true);
        setLoading(false);
        return;
      }
      
      toast({
        title: 'Welcome back!',

        description: 'Good luck at the tables!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      
      //  Check for pending invite before redirecting

      const inviteRedirect = await handlePendingInvite(false);
      navigate(inviteRedirect || '/');
    } catch (err: any) {
      console.error('Sign in error:', err);
      
      //  Record failed attempt

      await recordFailedLogin(email);
      
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email');
      } else if (err.code === 'auth/wrong-password') {
        setError('Incorrect password');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Try again later or reset your password.');
      } else {
        setError(err.message || 'Sign in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  //  Resend verification email

  const handleResendVerification = async () => {
    if (!auth.currentUser) return;
    
    setResendingVerification(true);
    try {
      await sendEmailVerification(auth.currentUser);
      toast({
        title: 'Verification email sent!',

        description: 'Check your inbox and spam folder.',
        status: 'success',
        duration: 5000,
      });
    } catch (err: any) {
      if (err.code === 'auth/too-many-requests') {
        toast({
          title: 'Too many requests',
          description: 'Please wait before requesting another email.',
          status: 'warning',
          duration: 4000,
        });
      } else {
        toast({
          title: 'Failed to send email',
          description: err.message,
          status: 'error',
          duration: 3000,
        });
      }
    } finally {
      setResendingVerification(false);
    }
  };

  //  Password reset handler

  const handlePasswordReset = async () => {
    if (!resetEmail.trim()) {
      toast({
        title: 'Enter your email',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    setResetLoading(true);
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSent(true);
      toast({
        title: 'Reset email sent!',

        description: 'Check your inbox for password reset instructions.',
        status: 'success',
        duration: 5000,
      });
    } catch (err: any) {
      if (err.code === 'auth/user-not-found') {
        toast({
          title: 'No account found',
          description: 'No account exists with this email.',
          status: 'error',
          duration: 3000,
        });
      } else {
        toast({
          title: 'Failed to send reset email',
          description: err.message,
          status: 'error',
          duration: 3000,
        });
      }
    } finally {
      setResetLoading(false);
    }
  };

  // ============================================================================
  // GOOGLE SIGN IN HANDLER
  // ============================================================================
  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);

    try {
      // ============================================================================
      //  USE NATIVE GOOGLE AUTH ON ALL PLATFORMS

      // Uses @codetrix-studio/capacitor-google-auth on iOS/Android
      // Falls back to popup on web automatically
      // ============================================================================
      const result = await signInWithGoogleNative();
      const user = result.user;

      // Check if user profile exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // New user - create profile and wallet
        const displayName = user.displayName || user.email?.split('@')[0] || 'Player';
        
        await userService.createUserProfile(
          user.uid, 
          user.email || '', 
          displayName,
          referrerId || undefined
        );
        await walletService.createWallet(user.uid);
        await recordRulesAcceptance(user.uid);
        
        //  Credit $5 PSS Coins referral bonus with 1x playthrough

        if (referralCodeValid && referralCode) {
          await creditReferralBonusAPI(user.uid);
          toast({
            title: 'Account Created with Bonus!',

            description: `Welcome ${displayName}! 1,000 PlayCoins + 5 FREE PSS Coins bonus!`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Account Created!',

            description: `Welcome ${displayName}! 1,000 PlayCoins credited!`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }
        
        //  Handle pending invite for new users (store for after onboarding)

        await handlePendingInvite(true);
        
        // Navigate to onboarding for new users
        navigate('/onboarding');
        return;
      } else {
        //  Check if 2FA is required for existing user

        const requires2FA = await check2FARequired(user.email || "");
        
        if (requires2FA) {
          setPendingCredential(result);
          setShow2FAModal(true);
          setGoogleLoading(false);
          return;
        }
        
        toast({
          title: 'Welcome back!',

          description: 'Good luck at the tables!',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }

      //  Check for pending invite before redirecting

      const inviteRedirect = await handlePendingInvite(false);
      navigate(inviteRedirect || '/');
    } catch (err: any) {
      console.error('Google sign in error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled');
      } else {
        setError(err.message || 'Google sign in failed');
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  // ============================================================================
  // APPLE SIGN IN HANDLER
  // ============================================================================
  const handleAppleSignIn = async () => {
    setError('');
    setAppleLoading(true);

    try {
      const result = await signInWithAppleNative();
      const user = result.user;

      // Check if user profile exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));

      if (!userDoc.exists()) {
        // New user - create profile and wallet
        const displayName = user.displayName || user.email?.split('@')[0] || 'Player';

        await userService.createUserProfile(
          user.uid,
          user.email || '',
          displayName,
          referrerId || undefined
        );
        await walletService.createWallet(user.uid);
        await recordRulesAcceptance(user.uid);

        // Credit referral bonus if applicable
        if (referralCodeValid && referralCode) {
          await creditReferralBonusAPI(user.uid);
          toast({
            title: 'Account Created with Bonus!',
            description: `Welcome ${displayName}! 1,000 PlayCoins + 5 FREE PSS Coins bonus!`,
            status: 'success',
            duration: 4000,
            isClosable: true,
          });
        } else {
          toast({
            title: 'Account Created!',
            description: `Welcome ${displayName}! 1,000 PlayCoins credited!`,
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        }

        // Handle pending invite for new users
        await handlePendingInvite(true);

        navigate('/onboarding');
        return;
      } else {
        // Check if 2FA is required for existing user
        const requires2FA = await check2FARequired(user.email || "");

        if (requires2FA) {
          setPendingCredential(result);
          setShow2FAModal(true);
          setAppleLoading(false);
          return;
        }

        toast({
          title: 'Welcome back!',
          description: 'Good luck at the tables!',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }

      // Check for pending invite before redirecting
      const inviteRedirect = await handlePendingInvite(false);
      navigate(inviteRedirect || '/');
    } catch (err: any) {
      console.error('Apple sign in error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled');
      } else {
        setError(err.message || 'Apple sign in failed');
      }
    } finally {
      setAppleLoading(false);
    }
  };

  // ============================================================================
  // SIGN UP HANDLER
  // ============================================================================
  const handleSignUp = async () => {
    setError('');

    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    if (!email.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    if (!username.trim()) {
      setError('Username is required');
      return;
    }
    if (username.length < 3) {
      setError('Username must be at least 3 characters');
      return;
    }
    if (username.length > 20) {
      setError('Username must be less than 20 characters');
      return;
    }
    if (!isContentClean(username)) {
      setError(getFilterMessage('username'));
      return;
    }
    if (!password) {
      setError('Password is required');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (!requireRules()) return;

    setLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const userId = userCredential.user.uid;

      // Stash the chosen username so onboarding can prefill it even when
      // the profile write below is still in flight (slow backend) - the
      // onboarding completion write persists it for real.
      try { sessionStorage.setItem('pss:signupUsername', username.trim()); } catch { /* noop */ }

      // The follow-up steps must never strand the spinner: the verify page
      // has a resend button for the email, and the profile/wallet writes
      // complete in the background if the backend is slow to acknowledge
      // (their idempotency reads were hanging signups on a congested
      // emulator). Bounded wait: a healthy backend finishes well inside it.
      // Track the verification send SEPARATELY. It used to sit inside a
      // Promise.allSettled whose result was thrown away, so a send that
      // FAILED was silently swallowed and the user was still told
      // "Verification email sent" - then waited forever for a mail that
      // never left. Now a failure is surfaced and pointed at Resend.
      let verifyEmailFailed = false;
      const verifyPromise = sendEmailVerification(userCredential.user)
        .catch((err: any) => {
          verifyEmailFailed = true;
          console.error('[SIGNUP] verification email FAILED to send:', err?.code || err);
        });

      const followUps = Promise.allSettled([
        verifyPromise,
        userService.createUserProfile(userId, email, username, referrerId || undefined)
          .then(() => recordRulesAcceptance(userId))
          .then(() => walletService.createWallet(userId))
          .then(() => (referralCodeValid && referralCode) ? creditReferralBonusAPI(userId) : undefined)
          .catch(err => console.error('[SIGNUP] Profile/wallet setup error (will retry on next load):', err)),
      ]);
      await Promise.race([followUps, new Promise(res => setTimeout(res, 6000))]);

      if (referralCodeValid && referralCode) {
        toast({
          title: 'Account created!',

          description: `Check your email to verify. 1,000 PlayCoins + 5 FREE PSS Coins bonus!`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Account created!',

          description: `Check your email to verify. 1,000 PlayCoins credited!`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      }

      //  Show verification notice - honestly. Claiming the mail was sent
      //  when it wasn't leaves the user stuck on a screen they cannot pass.
      if (verifyEmailFailed) {
        toast({
          title: "We couldn't send the verification email",
          description: 'Your account was created. Tap "Resend Email" on the next screen, and check your spam folder.',
          status: 'warning',
          duration: 12000,
          isClosable: true,
        });
      } else {
        toast({
          title: 'Verification email sent',
          description: 'Please verify your email before signing in - check your spam folder if it does not arrive.',
          status: 'info',
          duration: 8000,
          isClosable: true,
        });
      }

      //  Handle pending invite for new users (store for after onboarding).
      // Bounded for the same reason as above - never strand the spinner.
      await Promise.race([
        handlePendingInvite(true).catch(err => console.error('[SIGNUP] Pending invite error:', err)),
        new Promise(res => setTimeout(res, 2000)),
      ]);

      setEmail('');
      setPassword('');
      setUsername('');
      
      //  ENTERPRISE: Redirect to email verification page

      // User must verify email before proceeding to onboarding
      navigate('/verify-email');
    } catch (err: any) {
      console.error('Sign up error:', err);
      let errorMessage = err.message || 'Sign up failed';

      if (errorMessage.includes('email-already-in-use')) {
        errorMessage = 'This email is already registered';
      } else if (errorMessage.includes('invalid-email')) {
        errorMessage = 'Invalid email address';
      } else if (errorMessage.includes('weak-password')) {
        errorMessage = 'Password is too weak (min 6 characters)';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  //  FIX: Validate referral code with debounce and store referrer ID

  const handleReferralCodeChange = (code: string) => {
    const upperCode = code.toUpperCase();
    setReferralCode(upperCode);
    setReferralCodeValid(null);
    setReferrerId(null);
    
    if (upperCode.length >= 6) {
      setValidatingCode(true);
      userService.findUserByReferralCode(upperCode).then(userId => {
        setReferralCodeValid(!!userId);
        setReferrerId(userId);
        setValidatingCode(false);
      }).catch(() => {
        setReferralCodeValid(false);
        setReferrerId(null);
        setValidatingCode(false);
      });
    }
  };

  return (
    <Box
      minH="100vh"
      w="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={{ base: 4, md: 0 }}
      bg="linear-gradient(180deg, #1a0a0a 0%, #2d1810 50%, #1a0a0a 100%)"
      position="relative"
      overflow="hidden"
    >
      {/* Background Pattern */}
      <Box
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        opacity={0.05}
        backgroundImage="repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,215,0,0.05) 35px, rgba(255,215,0,0.05) 70px)"
        pointerEvents="none"
      />

      {/* Decorative Cards */}
      <Box
        position="absolute"
        top="10%"
        left="5%"
        fontSize="8xl"
        opacity={0.15}
        animation={`${float} 4s ease-in-out infinite`}
        display={{ base: 'none', lg: 'block' }}
      >
        🃏
      </Box>
      <Box
        position="absolute"
        bottom="15%"
        right="5%"
        fontSize="8xl"
        opacity={0.15}
        animation={`${float} 5s ease-in-out infinite`}
        sx={{ animationDelay: "1s" }}
        display={{ base: 'none', lg: 'block' }}
      >
        

      </Box>

      {/*  FREE TO PLAY - Floating Text Elements */}

      <Text
        position="absolute"
        top="8%"
        right="8%"
        fontSize={{ base: '2xl', md: '4xl', lg: '5xl' }}
        fontWeight="black"
        color="cyan.400"
        opacity={0.15}
        animation={`${pulseBig} 4s ease-in-out infinite`}
        letterSpacing="wider"
        textShadow="0 0 30px rgba(0,217,217,0.3)"
        display={{ base: 'none', md: 'block' }}
        transform="rotate(-8deg)"
      >
        FREE TO PLAY
      </Text>
      
      <Text
        position="absolute"
        top="25%"
        left="3%"
        fontSize={{ base: 'xl', md: '3xl', lg: '4xl' }}
        fontWeight="black"
        color="green.400"
        opacity={0.12}
        animation={`${floatSlow} 6s ease-in-out infinite`}
        letterSpacing="wider"
        textShadow="0 0 25px rgba(34,197,94,0.3)"
        display={{ base: 'none', lg: 'block' }}
        transform="rotate(5deg)"
      >
        FREE TO PLAY
      </Text>
      
      <Text
        position="absolute"
        bottom="25%"
        left="2%"
        fontSize={{ base: '2xl', md: '4xl', lg: '5xl' }}
        fontWeight="black"
        color="purple.400"
        opacity={0.12}
        animation={`${floatDrift} 7s ease-in-out infinite`}
        sx={{ animationDelay: "2s" }}
        letterSpacing="wider"
        textShadow="0 0 30px rgba(168,85,247,0.3)"
        display={{ base: 'none', lg: 'block' }}
        transform="rotate(-5deg)"
      >
        FREE TO PLAY
      </Text>
      
      <Text
        position="absolute"
        bottom="35%"
        right="3%"
        fontSize={{ base: 'xl', md: '3xl', lg: '4xl' }}
        fontWeight="black"
        color="yellow.400"
        opacity={0.12}
        animation={`${pulseBig} 5s ease-in-out infinite`}
        sx={{ animationDelay: "1.5s" }}
        letterSpacing="wider"
        textShadow="0 0 25px rgba(234,179,8,0.3)"
        display={{ base: 'none', lg: 'block' }}
        transform="rotate(6deg)"
      >
        FREE TO PLAY
      </Text>
      
      <Text
        position="absolute"
        top="45%"
        right="2%"
        fontSize={{ base: 'lg', md: '2xl', lg: '3xl' }}
        fontWeight="black"
        color="pink.400"
        opacity={0.1}
        animation={`${floatSlow} 8s ease-in-out infinite`}
        sx={{ animationDelay: "3s" }}
        letterSpacing="wider"
        textShadow="0 0 20px rgba(236,72,153,0.3)"
        display={{ base: 'none', lg: 'block' }}
        transform="rotate(-10deg)"
      >
        FREE TO PLAY
      </Text>

      {/* Mobile: Smaller FREE TO PLAY */}
      <Text
        position="absolute"
        top="5%"
        right="5%"
        fontSize="lg"
        fontWeight="black"
        color="cyan.400"
        opacity={0.2}
        animation={`${pulseBig} 3s ease-in-out infinite`}
        letterSpacing="wide"
        display={{ base: 'block', md: 'none' }}
        transform="rotate(-5deg)"
      >
        FREE TO PLAY
      </Text>
      
      <Text
        position="absolute"
        bottom="20%"
        left="5%"
        fontSize="lg"
        fontWeight="black"
        color="green.400"
        opacity={0.2}
        animation={`${pulseBig} 4s ease-in-out infinite`}
        sx={{ animationDelay: "1s" }}
        letterSpacing="wide"
        display={{ base: 'block', md: 'none' }}
        transform="rotate(5deg)"
      >
        FREE TO PLAY
      </Text>

      <Container maxW="md" position="relative" zIndex={1}>
        <VStack spacing={6} align="stretch">
          {/*  INVITE REDIRECT BANNER */}

          {(isInviteRedirect || hasPendingInviteToken) && (
            <Box
              bg="linear-gradient(135deg, rgba(0,217,217,0.2) 0%, rgba(0,136,170,0.2) 100%)"
              border="2px solid"
              borderColor="cyan.500"
              borderRadius="xl"
              p={4}
              textAlign="center"
            >
              <HStack justify="center" spacing={2} mb={1}>
                <Text fontSize="xl"></Text>

                <Text color="cyan.300" fontWeight="bold">
                  Table Invite Pending!
                </Text>
              </HStack>
              <Text color="gray.300" fontSize="sm">
                Sign in to join your friend at the poker table
              </Text>
            </Box>
          )}

          {/*  LOCKOUT WARNING */}

          {lockoutUntil && (
            <Box
              bg="linear-gradient(135deg, rgba(220,38,38,0.2) 0%, rgba(153,27,27,0.2) 100%)"
              border="2px solid"
              borderColor="red.500"
              borderRadius="xl"
              p={4}
              textAlign="center"
            >
              <VStack spacing={2}>
                <HStack justify="center" spacing={2}>
                  <Text fontSize="xl"></Text>

                  <Text color="red.300" fontWeight="bold">
                    Account Temporarily Locked
                  </Text>
                </HStack>
                <Text color="gray.300" fontSize="sm">
                  Too many failed login attempts. Try again in:
                </Text>
                <Text color="red.400" fontSize="2xl" fontWeight="bold" fontFamily="mono">
                  {lockoutCountdown}
                </Text>
              </VStack>
            </Box>
          )}

          {/* Branding */}
          <Box 
            textAlign="center"
            py={6}
            px={4}
            bg="linear-gradient(135deg, rgba(139,0,0,0.3) 0%, rgba(0,0,0,0.5) 50%, rgba(139,0,0,0.3) 100%)"
            borderRadius="2xl"
            border="2px solid"
            borderColor="yellow.600"
            boxShadow="0 0 40px rgba(255,215,0,0.2)"
          >
            <HStack justify="center" spacing={2} mb={2}>
              <Text fontSize="3xl"></Text>

              <Text fontSize="3xl"></Text>

            </HStack>
            <Heading 
              color="yellow.400" 
              size="xl"
              textShadow="0 0 20px rgba(255,215,0,0.5)"
              fontWeight="black"
            >
              PokerSweepstars
            </Heading>
            <Text color="red.300" fontSize="md" fontWeight="bold" textTransform="uppercase" letterSpacing="wider">
              Social Poker Platform
            </Text>
          </Box>

          {/*  MAIN SELLING POINTS */}

          <Box
            bg="linear-gradient(135deg, rgba(0,217,217,0.1) 0%, rgba(212,175,55,0.1) 100%)"
            border="2px solid"
            borderColor="cyan.600"
            borderRadius="xl"
            p={4}
            position="relative"
            overflow="hidden"
          >
            {/* Animated shimmer */}
            <Box
              position="absolute"
              top={0}
              left={-100}
              w="100px"
              h="100%"
              bg="linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)"
              animation={`${shimmer} 3s infinite`}
            />
            
            <VStack spacing={4}>
              {/* First Mobile App */}
              <HStack spacing={3} w="100%">
                <Box bg="rgba(0,217,217,0.2)" p={2} borderRadius="lg">
                  <Text fontSize="xl"></Text>

                </Box>
                <VStack align="start" spacing={0} flex={1}>
                  <Text color="cyan.300" fontWeight="bold" fontSize="sm">
                    Play Poker Anywhere
                  </Text>
                  <Text color="gray.400" fontSize="xs">
                    Play Anywhere - Your game, your way
                  </Text>
                </VStack>
              </HStack>

              {/* Training Tools */}
              <HStack spacing={3} w="100%">
                <Box bg="rgba(34,197,94,0.2)" p={2} borderRadius="lg">
                  <Text fontSize="xl"></Text>

                </Box>
                <VStack align="start" spacing={0} flex={1}>
                  <Text color="green.400" fontWeight="bold" fontSize="sm">
                    AI-Powered Training & Scoring
                  </Text>
                  <Text color="gray.400" fontSize="xs">
                    Custom-built algorithms to analyze your play & make you a pro
                  </Text>
                </VStack>
              </HStack>

              {/* Dual Currency */}
              <HStack spacing={3} w="100%">
                <Box bg="rgba(212,175,55,0.2)" p={2} borderRadius="lg">
                  <Text fontSize="xl"></Text>

                </Box>
                <VStack align="start" spacing={0} flex={1}>
                  <Text color="yellow.400" fontWeight="bold" fontSize="sm">
                    Play Free • Compete • Have Fun*
                  </Text>
                  <Text color="gray.400" fontSize="xs">
                    PlayCoins for fun • PSS Coins prizes where eligible
                  </Text>
                </VStack>
              </HStack>

              {/* Fair Play */}
              <HStack spacing={3} w="100%">
                <Box bg="rgba(139,92,246,0.2)" p={2} borderRadius="lg">
                  <Text fontSize="xl"></Text>

                </Box>
                <VStack align="start" spacing={0} flex={1}>
                  <Text color="purple.400" fontWeight="bold" fontSize="sm">
                    Certified Fair Play
                  </Text>
                  <Text color="gray.400" fontSize="xs">
                    RNG guaranteed • No bots • Real players only
                  </Text>
                </VStack>
              </HStack>
            </VStack>
          </Box>

          {/*  SHORT FOUNDER NOTE */}

          <Box
            bg="rgba(139,92,246,0.08)"
            border="1px solid"
            borderColor="purple.700"
            borderRadius="lg"
            p={3}
          >
            <VStack spacing={2}>
              <Text color="gray.300" fontSize="xs" textAlign="center" lineHeight="1.6">
                Built by a poker player who saw what the community was missing.{' '}
                <Text as="span" color="purple.300" fontWeight="bold">For players, by a player.</Text>
                {' '}Every feature, every update - driven by you.
              </Text>
              <HStack spacing={3} justify="center">
                <HStack spacing={1}>
                  <Text fontSize="xs">⭐</Text>
                  <Text color="yellow.400" fontSize="2xs" fontWeight="bold">Leave a review</Text>
                </HStack>
                <Text color="gray.600" fontSize="xs">•</Text>
                <Text color="gray.500" fontSize="2xs">We're here to stay 🃏</Text>
              </HStack>
            </VStack>
          </Box>

          {/* Auth Card */}
          <Box
            bg="linear-gradient(135deg, rgba(13,56,71,0.9) 0%, rgba(10,31,46,0.95) 100%)"
            borderRadius="2xl"
            border="2px solid"
            borderColor="cyan.700"
            p={6}
            boxShadow="0 0 30px rgba(0,217,217,0.1)"
          >
            <Tabs variant="soft-rounded" isFitted>
              <TabList mb={6} gap={2} bg="blackAlpha.300" p={1} borderRadius="xl">
                <Tab
                  color="gray.400"
                  fontWeight="bold"
                  _selected={{ 
                    bg: 'linear-gradient(135deg, #00d9d9 0%, #0088aa 100%)', 
                    color: 'white',
                    boxShadow: '0 0 15px rgba(0,217,217,0.5)'
                  }}
                >
                  Sign In
                </Tab>
                <Tab
                  color="gray.400"
                  fontWeight="bold"
                  _selected={{ 
                    bg: 'linear-gradient(135deg, #00d9d9 0%, #0088aa 100%)', 
                    color: 'white',
                    boxShadow: '0 0 15px rgba(0,217,217,0.5)'
                  }}
                >
                  Sign Up
                </Tab>
              </TabList>

              <TabPanels>
                {/* Sign In */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    {error && (
                      <Alert status="error" rounded="lg" bg="red.900" borderColor="red.500" border="1px solid">
                        <AlertIcon color="red.300" />
                        <Text fontSize="sm" color="red.200">{error}</Text>
                      </Alert>
                    )}

                    {/* Attempts remaining warning */}
                    {attemptsRemaining !== null && attemptsRemaining <= 2 && !lockoutUntil && (
                      <Alert status="warning" rounded="lg" bg="yellow.900" borderColor="yellow.500" border="1px solid">
                        <AlertIcon color="yellow.300" />
                        <Text fontSize="sm" color="yellow.200">
                           {attemptsRemaining} login attempt{attemptsRemaining !== 1 ? 's' : ''} remaining before lockout

                        </Text>
                      </Alert>
                    )}

                    {/* Google Sign In Button */}
                    <PokerButton
                      w="100%"
                      h="50px"
                      bg="white"
                      color="gray.700"
                      border="2px solid"
                      borderColor="gray.300"
                      _hover={{ bg: 'gray.100', borderColor: 'gray.400' }}
                      onClick={handleGoogleSignIn}
                      isLoading={googleLoading}
                      isDisabled={loading || googleLoading || !!lockoutUntil}
                      leftIcon={
                        <svg width="20" height="20" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      }
                    >
                      Continue with Google
                    </PokerButton>

                    {/* Apple Sign In Button */}
                    <PokerButton
                      w="100%"
                      h="50px"
                      bg="black"
                      color="white"
                      border="2px solid"
                      borderColor="gray.600"
                      _hover={{ bg: 'gray.900', borderColor: 'gray.500' }}
                      onClick={handleAppleSignIn}
                      isLoading={appleLoading}
                      isDisabled={loading || googleLoading || appleLoading || !!lockoutUntil}
                      leftIcon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                        </svg>
                      }
                    >
                      Continue with Apple
                    </PokerButton>

                    <HStack>
                      <Divider borderColor="gray.600" />
                      <Text color="gray.500" fontSize="sm" whiteSpace="nowrap">or</Text>
                      <Divider borderColor="gray.600" />
                    </HStack>

                    <PokerInput
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      isDisabled={loading || googleLoading || appleLoading || !!lockoutUntil}
                    />

                    <PokerInput
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      isDisabled={loading || googleLoading || appleLoading || !!lockoutUntil}
                    />

                    <PokerButton
                      variant="primary"
                      w="100%"
                      h="50px"
                      isLoading={loading}
                      onClick={handleSignIn}
                      isDisabled={loading || googleLoading || appleLoading || !!lockoutUntil}
                      bg="linear-gradient(135deg, #00d9d9 0%, #0088aa 100%)"
                      _hover={{ transform: 'scale(1.02)', boxShadow: '0 0 20px rgba(0,217,217,0.5)' }}
                    >
                      Sign In
                    </PokerButton>

                    {/*  EMAIL VERIFICATION WARNING */}

                    {showVerificationWarning && (
                      <Alert status="warning" rounded="lg" bg="yellow.900" borderColor="yellow.500" border="1px solid">
                        <AlertIcon color="yellow.300" />
                        <VStack align="start" spacing={2} flex={1}>
                          <Text fontSize="sm" color="yellow.200" fontWeight="bold">
                            Email not verified
                          </Text>
                          <Text fontSize="xs" color="yellow.100">
                            Please check your inbox and verify your email before signing in.
                          </Text>
                          <PokerButton
                            size="sm"
                            variant="secondary"
                            onClick={handleResendVerification}
                            isLoading={resendingVerification}
                          >
                            Resend Verification Email
                          </PokerButton>
                        </VStack>
                      </Alert>
                    )}

                    {/*  FORGOT PASSWORD LINK */}

                    <Text
                      color="cyan.400"
                      fontSize="sm"
                      textAlign="center"
                      cursor="pointer"
                      _hover={{ textDecoration: 'underline', color: 'cyan.300' }}
                      onClick={() => {
                        setResetEmail(email);
                        setResetSent(false);
                        onResetOpen();
                      }}
                    >
                      Forgot password?
                    </Text>
                  </VStack>
                </TabPanel>

                {/* Sign Up */}
                <TabPanel px={0}>
                  <VStack spacing={4} align="stretch">
                    {error && (
                      <Alert status="error" rounded="lg" bg="red.900" borderColor="red.500" border="1px solid">
                        <AlertIcon color="red.300" />
                        <Text fontSize="sm" color="red.200">{error}</Text>
                      </Alert>
                    )}

                    {/* Google Sign Up Button */}
                    <PokerButton
                      w="100%"
                      h="50px"
                      bg="white"
                      color="gray.700"
                      border="2px solid"
                      borderColor="gray.300"
                      _hover={{ bg: 'gray.100', borderColor: 'gray.400' }}
                      onClick={() => { if (requireRules()) handleGoogleSignIn(); }}
                      isLoading={googleLoading}
                      isDisabled={loading || googleLoading || appleLoading}
                      leftIcon={
                        <svg width="20" height="20" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      }
                    >
                      Continue with Google
                    </PokerButton>

                    {/* Apple Sign Up Button */}
                    <PokerButton
                      w="100%"
                      h="50px"
                      bg="black"
                      color="white"
                      border="2px solid"
                      borderColor="gray.600"
                      _hover={{ bg: 'gray.900', borderColor: 'gray.500' }}
                      onClick={() => { if (requireRules()) handleAppleSignIn(); }}
                      isLoading={appleLoading}
                      isDisabled={loading || googleLoading || appleLoading}
                      leftIcon={
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                        </svg>
                      }
                    >
                      Continue with Apple
                    </PokerButton>

                    <HStack>
                      <Divider borderColor="gray.600" />
                      <Text color="gray.500" fontSize="sm" whiteSpace="nowrap">or</Text>
                      <Divider borderColor="gray.600" />
                    </HStack>

                    <PokerInput
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      isDisabled={loading || googleLoading || appleLoading}
                    />

                    <PokerInput
                      type="text"
                      placeholder="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                      isDisabled={loading || googleLoading}
                      maxLength={20}
                    />

                    <PokerInput
                      type="password"
                      placeholder="Password (min 6 characters)"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      isDisabled={loading || googleLoading}
                    />

                    {/* Referral Code Section */}
                    {!showReferralInput ? (
                      <Text
                        color="cyan.400"
                        fontSize="sm"
                        textAlign="center"
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={() => setShowReferralInput(true)}
                      >
                        Have a referral code? 

                      </Text>
                    ) : (
                      <Box
                        p={3}
                        bg="rgba(0,217,217,0.1)"
                        borderRadius="lg"
                        border="1px solid"
                        borderColor={
                          referralCodeValid === true ? 'green.500' :
                          referralCodeValid === false ? 'red.500' : 'cyan.700'
                        }
                      >
                        <VStack spacing={2}>
                          <HStack w="100%">
                            <PokerInput
                              placeholder="Enter referral code"
                              value={referralCode}
                              onChange={(e) => handleReferralCodeChange(e.target.value)}
                              isDisabled={loading || googleLoading}
                              textTransform="uppercase"
                            />
                            {validatingCode && (
                              <Text color="cyan.400" fontSize="sm">⏳</Text>
                            )}
                            {referralCodeValid === true && (
                              <Text color="green.400" fontSize="lg"></Text>

                            )}
                            {referralCodeValid === false && (
                              <Text color="red.400" fontSize="lg"></Text>

                            )}
                          </HStack>
                          {referralCodeValid === true && (
                            <Text color="green.400" fontSize="xs" textAlign="center">
                               Valid code! You'll get 5 FREE PSS Coins on signup!

                            </Text>
                          )}
                          {referralCodeValid === false && (
                            <Text color="red.400" fontSize="xs" textAlign="center">
                              Invalid referral code
                            </Text>
                          )}
                        </VStack>
                      </Box>
                    )}

                    {/* Official Rules acceptance */}
                    <Box
                      p={3}
                      bg="rgba(255,215,0,0.06)"
                      borderRadius="lg"
                      border="1px solid"
                      borderColor={acceptedRules ? 'green.500' : 'rgba(255,215,0,0.35)'}
                    >
                      <Checkbox
                        isChecked={acceptedRules}
                        onChange={(e) => setRulesAccepted(e.target.checked)}
                        colorScheme="green"
                        size="md"
                        alignItems="flex-start"
                        isDisabled={loading || googleLoading || appleLoading}
                      >
                        <Text fontSize="xs" color="gray.300" lineHeight="1.4">
                          I am 18 or older and I have read and accept the{' '}
                          <Text
                            as="span"
                            color="cyan.400"
                            fontWeight="semibold"
                            cursor="pointer"
                            _hover={{ textDecoration: 'underline' }}
                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRulesOpen(); }}
                          >
                            Official Sweepstakes Rules
                          </Text>
                          . No purchase necessary. Void where prohibited.
                        </Text>
                      </Checkbox>
                    </Box>

                    <PokerButton
                      variant="primary"
                      w="100%"
                      h="50px"
                      isLoading={loading}
                      onClick={handleSignUp}
                      isDisabled={loading || googleLoading}
                      bg="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                      _hover={{ transform: 'scale(1.02)', boxShadow: '0 0 20px rgba(34,197,94,0.5)' }}
                    >
                      Create Account
                    </PokerButton>

                    {/* Terms Agreement */}
                    <Text color="gray.500" fontSize="2xs" textAlign="center">
                      By signing up, you agree to our{' '}
                      <Text 
                        as="span" 
                        color="cyan.400" 
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={() => navigate('/legal?tab=terms')}
                      >
                        Terms of Service
                      </Text>
                      {' '}and{' '}
                      <Text 
                        as="span" 
                        color="cyan.400" 
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                        onClick={() => navigate('/legal?tab=privacy')}
                      >
                        Privacy Policy
                      </Text>
                    </Text>
                  </VStack>
                </TabPanel>
              </TabPanels>
            </Tabs>
          </Box>

          {/* Security & Compliance Badges */}
          <VStack spacing={4}>
            <HStack spacing={3} flexWrap="wrap" justify="center">
              {/* SSL Badge */}
              <Box
                bg="linear-gradient(135deg, #22543d 0%, #276749 100%)"
                px={3}
                py={2}
                borderRadius="lg"
                border="1px solid"
                borderColor="green.600"
              >
                <VStack spacing={0}>
                  <Text fontSize="xs" fontWeight="bold" color="green.200"> 256-bit SSL</Text>

                  <Text color="green.300" fontSize="2xs">Encrypted</Text>
                </VStack>
              </Box>

              {/* KYC Badge */}
              <Box
                bg="linear-gradient(135deg, #553c9a 0%, #6b46c1 100%)"
                px={3}
                py={2}
                borderRadius="lg"
                border="1px solid"
                borderColor="purple.500"
              >
                <VStack spacing={0}>
                  <Text fontSize="xs" fontWeight="bold" color="purple.200"> KYC</Text>

                  <Text color="purple.300" fontSize="2xs">Verified</Text>
                </VStack>
              </Box>

              {/* 18+ Badge */}
              <Box
                bg="linear-gradient(135deg, #9c4221 0%, #c05621 100%)"
                px={3}
                py={2}
                borderRadius="lg"
                border="1px solid"
                borderColor="orange.500"
              >
                <VStack spacing={0}>
                  <Text fontSize="xs" fontWeight="bold" color="orange.200">18+</Text>
                  <Text color="orange.300" fontSize="2xs">Only</Text>
                </VStack>
              </Box>
            </HStack>

            {/* Security Statement */}
            <Box
              bg="linear-gradient(135deg, rgba(10,31,46,0.9) 0%, rgba(5,20,30,0.95) 100%)"
              p={3}
              borderRadius="xl"
              border="1px solid"
              borderColor="gray.700"
            >
              <Text color="gray.500" fontSize="2xs" textAlign="center" lineHeight="1.5">
                PlayCoins purchases are handled by {isStoreSafe() ? 'the App Store' : 'our payment partner'}; we never see your card or wallet keys.
                Prize eligibility checks are powered by Didit. Your personal data is protected with 256-bit SSL encryption.
              </Text>
            </Box>

            {/* Fair Play deterrent - every claim here maps to a real system:
                device signal collection at signup, decision-timing forensics
                at the tables, and the nightly scan + ledger audit + KYC gate
                at redemption. Worded to make a bot farm feel pre-caught. */}
            <Box
              bg="rgba(0,217,217,0.05)"
              p={3}
              borderRadius="xl"
              border="1px solid"
              borderColor="rgba(0,217,217,0.25)"
            >
              <Text color="cyan.400" fontWeight="bold" fontSize="2xs" textAlign="center" letterSpacing="0.15em">
                🛡 PSS FAIR PLAY SHIELD
              </Text>
              <Text color="gray.500" fontSize="2xs" textAlign="center" lineHeight="1.5" mt={1}>
                Every account is screened by multi-layer bot detection: device forensics at
                sign-up, behavioral and timing analysis on every hand, and automated
                play-pattern audits before any redemption. Automated play is detected,
                frozen, and forfeited.
              </Text>
            </Box>

            {/* Legal Disclosure */}
            <Box
              bg="linear-gradient(135deg, rgba(139,0,0,0.15) 0%, rgba(0,0,0,0.2) 100%)"
              p={3}
              borderRadius="xl"
              border="1px solid"
              borderColor="red.900"
            >
              <VStack spacing={2}>
                <Text color="red.400" fontWeight="bold" fontSize="xs" textAlign="center">
                   MUST BE 18+ TO PLAY • PLAY RESPONSIBLY

                </Text>
                <Text color="gray.600" fontSize="2xs" textAlign="center" lineHeight="1.4">
                  PokerSweepStars is a social poker platform. Virtual currency for entertainment purposes.
                  Free entries available. Void where prohibited. PSS Coins redeemable for prizes.
                  PlayCoins are for entertainment only and have no cash value.
                </Text>
                <HStack justify="center" spacing={3} flexWrap="wrap">
                  <Text 
                    color="gray.500" 
                    fontSize="2xs" 
                    cursor="pointer" 
                    _hover={{ color: 'cyan.400', textDecoration: 'underline' }}
                    onClick={() => navigate('/legal?tab=terms')}
                  >
                    Terms of Service
                  </Text>
                  <Text color="gray.600" fontSize="2xs">•</Text>
                  <Text 
                    color="gray.500" 
                    fontSize="2xs" 
                    cursor="pointer" 
                    _hover={{ color: 'cyan.400', textDecoration: 'underline' }}
                    onClick={() => navigate('/legal?tab=privacy')}
                  >
                    Privacy Policy
                  </Text>
                  <Text color="gray.600" fontSize="2xs">•</Text>
                  <Text 
                    color="gray.500" 
                    fontSize="2xs" 
                    cursor="pointer" 
                    _hover={{ color: 'cyan.400', textDecoration: 'underline' }}
                    onClick={() => navigate('/legal?tab=rules')}
                  >
                    Official Rules
                  </Text>
                  <Text color="gray.600" fontSize="2xs">•</Text>
                  <Text
                    color="gray.500"
                    fontSize="2xs"
                    cursor="pointer"
                    _hover={{ color: 'cyan.400', textDecoration: 'underline' }}
                    onClick={() => navigate('/legal?tab=fairness')}
                  >
                    Fair Play
                  </Text>
                </HStack>

                {/* Official accounts - useful here because this is the page
                    people land on before they have an account. */}
                <SocialLinks size="sm" color="gray.600" hoverColor="#22d3ee" />
              </VStack>
            </Box>


            {/* Bug Reporting Notice */}
            <Box
              bg="rgba(0, 217, 217, 0.08)"
              p={3}
              borderRadius="lg"
              border="1px solid"
              borderColor="cyan.900"
            >
              <VStack spacing={1}>
                <Text color="cyan.400" fontWeight="semibold" fontSize="xs" textAlign="center">
                   Found a Bug? Help Us Improve!

                </Text>
                <Text color="gray.400" fontSize="2xs" textAlign="center" lineHeight="1.4">
                  Report security vulnerabilities or gameplay issues through{' '}
                  <Text as="span" color="cyan.400" fontWeight="bold">Profile → Support</Text>
                  {' '}after logging in. We review all reports and respond quickly.
                </Text>
              </VStack>
            </Box>

            {/* Copyright */}
            <Text color="gray.600" fontSize="2xs" textAlign="center">
              © {new Date().getFullYear()} PokerSweepStars. All rights reserved.
            </Text>
          </VStack>
        </VStack>
      </Container>

      {/*  PASSWORD RESET MODAL */}

      {/* Official Sweepstakes Rules summary - accept from here or read the full rules */}
      <Modal isOpen={isRulesOpen} onClose={onRulesClose} isCentered size={{ base: 'full', sm: 'lg' }} scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="#0f1a1a" border="1px solid" borderColor="rgba(255,215,0,0.35)" color="white">
          <ModalHeader color="#FFD700">Official Sweepstakes Rules</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3} fontSize="sm" color="gray.200">
              <Box p={3} bg="rgba(255,215,0,0.08)" borderRadius="md" borderLeft="3px solid #FFD700">
                <Text fontWeight="bold" color="#FFD700">NO PURCHASE NECESSARY TO ENTER OR WIN.</Text>
                <Text fontSize="xs" color="gray.300">A purchase will not increase your chances of winning. Void where prohibited.</Text>
              </Box>
              <Text><b>What this is.</b> PokerSweepStars is a free-to-play social poker game. PlayCoins are for fun only and have no cash value. Eligible players may also take part in promotional sweepstakes using PSS Coins.</Text>
              <Text><b>Free entry.</b> PSS Coins are never sold. You can get them free through daily bonuses, training rewards, and a free Alternative Method of Entry (AMOE). Full instructions are in the Free Entry section of the rules.</Text>
              <Text><b>Eligibility.</b> You must be 18 or older, a legal resident of the United States, and located in an eligible state. Players in restricted states can play with PlayCoins only.</Text>
              <Text><b>Prizes.</b> Eligible players may redeem PSS Coins for prizes under the Official Rules, subject to playthrough, limits, and identity verification.</Text>
              <Text><b>Sponsor.</b> The sweepstakes are run solely by PokerSweepStars. Apple Inc. is not a sponsor of, and is not involved in any way with, these sweepstakes.</Text>
              <Link
                color="cyan.400"
                fontWeight="semibold"
                href="/legal?tab=rules"
                onClick={(e) => { e.preventDefault(); onRulesClose(); navigate('/legal?tab=rules'); }}
              >
                Read the full Official Rules
              </Link>
            </VStack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" color="gray.300" onClick={onRulesClose}>Not now</Button>
            <Button
              bg="linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
              color="white"
              _hover={{ opacity: 0.9 }}
              onClick={() => { setRulesAccepted(true); setError(''); onRulesClose(); }}
            >
              I accept the rules
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isResetOpen} onClose={onResetClose} isCentered>
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg={POKER_COLORS.darkBase} borderColor="cyan.800" border="1px solid">
          <ModalHeader color={POKER_COLORS.cyan}>Reset Password</ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody>
            {resetSent ? (
              <VStack spacing={4} py={4}>
                <Text fontSize="4xl"></Text>

                <Text color={POKER_COLORS.accentCyan} textAlign="center">
                  Check your email for password reset instructions.
                </Text>
                <Text color="gray.500" fontSize="sm" textAlign="center">
                  If you don't see it, check your spam folder.
                </Text>
              </VStack>
            ) : (
              <VStack spacing={4}>
                <Text color={POKER_COLORS.accentCyan}>
                  Enter your email address and we'll send you a link to reset your password.
                </Text>
                <PokerInput
                  type="email"
                  placeholder="Enter your email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {resetSent ? (
              <PokerButton variant="secondary" onClick={onResetClose}>
                Close
              </PokerButton>
            ) : (
              <HStack spacing={3}>
                <PokerButton variant="secondary" onClick={onResetClose}>
                  Cancel
                </PokerButton>
                <PokerButton
                  variant="primary"
                  onClick={handlePasswordReset}
                  isLoading={resetLoading}
                >
                  Send Reset Email
                </PokerButton>
              </HStack>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* =========================================================================
           TWO-FACTOR AUTHENTICATION MODAL

          ========================================================================= */}
      <Modal 
        isOpen={show2FAModal} 
        onClose={() => {
          setShow2FAModal(false);
          setTwoFactorCode('');
          setTwoFactorError('');
          setPendingCredential(null);
          setShowEmailRecovery(false);
          setEmailRecoveryCode('');
          setEmailRecoverySent(false);
        }} 
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg={POKER_COLORS.darkBase} borderColor="cyan.800" border="2px solid">
          <ModalHeader color={POKER_COLORS.cyan}>
            <HStack>
              <Text></Text>

              <Text>{showEmailRecovery ? 'Email Recovery' : 'Two-Factor Authentication'}</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton color="gray.400" />
          <ModalBody>
            {!showEmailRecovery ? (
              // =========== AUTHENTICATOR VIEW ===========
              <VStack spacing={4}>
                <Text color="gray.300" textAlign="center">
                  Enter the 6-digit code from your authenticator app, or use a backup code.
                </Text>
                
                {twoFactorError && (
                  <Alert status="error" rounded="lg" bg="red.900" borderColor="red.500" border="1px solid">
                    <AlertIcon color="red.300" />
                    <Text fontSize="sm" color="red.200">{twoFactorError}</Text>
                  </Alert>
                )}

                {attemptsRemaining !== null && attemptsRemaining <= 2 && (
                  <Alert status="warning" rounded="lg" bg="yellow.900" borderColor="yellow.500" border="1px solid">
                    <AlertIcon color="yellow.300" />
                    <Text fontSize="sm" color="yellow.200">
                       {attemptsRemaining} attempt{attemptsRemaining !== 1 ? 's' : ''} remaining

                    </Text>
                  </Alert>
                )}

                <Box>
                  <HStack justify="center">
                    <PinInput
                      value={twoFactorCode}
                      onChange={setTwoFactorCode}
                      size="lg"
                      otp
                      isDisabled={twoFactorLoading}
                    >
                      <PinInputField bg="gray.800" borderColor="cyan.700" color="white" />
                      <PinInputField bg="gray.800" borderColor="cyan.700" color="white" />
                      <PinInputField bg="gray.800" borderColor="cyan.700" color="white" />
                      <PinInputField bg="gray.800" borderColor="cyan.700" color="white" />
                      <PinInputField bg="gray.800" borderColor="cyan.700" color="white" />
                      <PinInputField bg="gray.800" borderColor="cyan.700" color="white" />
                    </PinInput>
                  </HStack>
                  <Text color="gray.500" fontSize="xs" textAlign="center" mt={2}>
                    Or enter your 8-character backup code
                  </Text>
                </Box>

                <Input
                  placeholder="Or enter backup code"
                  value={twoFactorCode.length > 6 ? twoFactorCode : ''}
                  onChange={(e) => setTwoFactorCode(e.target.value.toUpperCase())}
                  maxLength={8}
                  textAlign="center"
                  fontFamily="mono"
                  letterSpacing="wider"
                  bg="gray.800"
                  borderColor="gray.600"
                  color="white"
                  isDisabled={twoFactorLoading}
                  _placeholder={{ color: 'gray.500' }}
                />

                <Divider borderColor="gray.700" />

                <Link
                  color="cyan.400"
                  fontSize="sm"
                  onClick={() => {
                    setShowEmailRecovery(true);
                    setTwoFactorError('');
                  }}
                  _hover={{ textDecoration: 'underline' }}
                >
                  Lost your authenticator? Recover via email
                </Link>
              </VStack>
            ) : (
              // =========== EMAIL RECOVERY VIEW ===========
              <VStack spacing={4}>
                {!emailRecoverySent ? (
                  <>
                    <Text color="gray.300" textAlign="center">
                      We'll send a recovery code to your registered email address.
                    </Text>
                    
                    {emailRecoveryError && (
                      <Alert status="error" rounded="lg" bg="red.900" borderColor="red.500" border="1px solid">
                        <AlertIcon color="red.300" />
                        <Text fontSize="sm" color="red.200">{emailRecoveryError}</Text>
                      </Alert>
                    )}

                    <PokerButton
                      variant="primary"
                      w="100%"
                      onClick={handleRequestEmailRecovery}
                      isLoading={emailRecoveryLoading}
                    >
                      Send Recovery Code
                    </PokerButton>
                  </>
                ) : (
                  <>
                    <Text color="gray.300" textAlign="center">
                      Enter the 8-character code sent to your email.
                    </Text>
                    
                    {emailRecoveryError && (
                      <Alert status="error" rounded="lg" bg="red.900" borderColor="red.500" border="1px solid">
                        <AlertIcon color="red.300" />
                        <Text fontSize="sm" color="red.200">{emailRecoveryError}</Text>
                      </Alert>
                    )}

                    <Input
                      placeholder="Enter recovery code"
                      value={emailRecoveryCode}
                      onChange={(e) => setEmailRecoveryCode(e.target.value.toUpperCase())}
                      maxLength={8}
                      textAlign="center"
                      fontFamily="mono"
                      letterSpacing="wider"
                      fontSize="xl"
                      bg="gray.800"
                      borderColor="cyan.700"
                      color="white"
                      isDisabled={emailRecoveryLoading}
                      _placeholder={{ color: 'gray.500' }}
                    />

                    <PokerButton
                      variant="primary"
                      w="100%"
                      onClick={handleVerifyEmailRecovery}
                      isLoading={emailRecoveryLoading}
                      isDisabled={emailRecoveryCode.length !== 8}
                    >
                      Verify & Sign In
                    </PokerButton>

                    <Link
                      color="gray.500"
                      fontSize="sm"
                      onClick={handleRequestEmailRecovery}
                      _hover={{ color: 'cyan.400' }}
                    >
                      Resend code
                    </Link>
                  </>
                )}

                <Divider borderColor="gray.700" />

                <Link
                  color="gray.500"
                  fontSize="sm"
                  onClick={() => {
                    setShowEmailRecovery(false);
                    setEmailRecoveryError('');
                    setEmailRecoverySent(false);
                    setEmailRecoveryCode('');
                  }}
                  _hover={{ color: 'cyan.400' }}
                >
                  ← Back to authenticator
                </Link>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            {!showEmailRecovery && (
              <HStack spacing={3} w="100%">
                <PokerButton 
                  variant="secondary" 
                  flex={1}
                  onClick={() => {
                    setShow2FAModal(false);
                    setTwoFactorCode('');
                    setPendingCredential(null);
                  }}
                >
                  Cancel
                </PokerButton>
                <PokerButton
                  variant="primary"
                  flex={1}
                  onClick={handle2FAVerify}
                  isLoading={twoFactorLoading}
                  isDisabled={twoFactorCode.length < 6}
                >
                  Verify
                </PokerButton>
              </HStack>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default LoginPage;